import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import type { WatchQueryFetchPolicy } from '@apollo/client';

import GoogleMap, { googleMapDefaultOptions } from 'src/components/map/GoogleMap';

import type { Ad, Geometry, GeometryInput } from 'src/types/__generated__/graphql';

import { styledTag } from 'src/utils/styled';
import {
    fitMapToFrancePosition,
    fitMapToWoopenGeometry,
    geocodeByAddress,
    transformGoogleMapBoundsToGeoJSONBBox,
    transformGoogleMapBoundsToGeometry,
} from 'src/utils/googleMapUtils';
import { useMapAds } from '../hooks/useMapAds';
import { useClusters } from '../hooks/useClusters';
import { SearchAdsPageMapDrawing } from './SearchAdsPageMapDrawing';
import { SearchAdsPageMarker } from './SearchAdsPageMarker';
import { useAdSearchState } from '../contexts/AdSearchStateContext';

const Container = styledTag('div')`
    width: 100%;
    height: 100%;
`;

const maxZoom = 20;

interface Props {
    fetchPolicy: WatchQueryFetchPolicy;
}

export const SearchAdsPageMap: React.FC<Props> = ({ fetchPolicy }) => {
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [mouseIsOnMap, setMouseIsOnMap] = useState<boolean>(false);
    const [tilesLoaded, setTilesLoaded] = useState<boolean>(false);
    const [isDrawingMode, setIsDrawingMode] = useState<boolean>(false);
    const [boundsForClustering, setBoundsForClustering] = useState<
        { bounds: GeoJSON.BBox | undefined; zoom?: number } | undefined
    >(undefined);

    const {
        fitMapOnAdSearchStateChange,
        mapBounds,
        address,
        adsQueryVariables,
        isPolygonDisplayed,
        savedSearchId,
        viewMode,
        changeAdSearchState,
    } = useAdSearchState();
    const { ads, loading } = useMapAds({
        queryVariables: adsQueryVariables,
        fetchPolicy,
        mapBounds,
    });

    useEffect(() => {
        if (map) {
            google.maps.event.addListener(map, 'tilesloaded', () => {
                // map.getBounds() is undefined if tiles are not loaded
                setTilesLoaded(true);
            });
        }
    }, [map]);

    const hasMapAlreadyBeenFitted = useRef(false);
    // We reset the flag when the view mode change to list because the map will be repositioned when the user will go back to the map view
    useEffect(() => {
        if (viewMode === 'list') {
            hasMapAlreadyBeenFitted.current = false;
        }
    }, [viewMode]);

    useEffect(() => {
        if (!tilesLoaded || !map || !fitMapOnAdSearchStateChange) {
            return;
        }

        // If the map has never been positioned and we have map bounds, we fit the map to the bounds
        if (!hasMapAlreadyBeenFitted.current && mapBounds) {
            fitMapToWoopenGeometry(map, mapBounds);
            hasMapAlreadyBeenFitted.current = true;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- no need to wait for the result
            debouncedUpdateQueryVariables(false); // to be sure places = [the viewport of the map]
            return;
        }

        // If the map has never been positioned and we have a address query param, we fit the map to the location
        if (!hasMapAlreadyBeenFitted.current && address) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- no need to wait for the result
            fitMapByAddress(address);
            return;
        }

        if (adsQueryVariables.places && (adsQueryVariables.places as Geometry[]).length > 0) {
            fitMapToWoopenGeometry(map, (adsQueryVariables.places as Geometry[])[0]);
        } else {
            // The user can search without place, if it's the case, we center on entire France
            fitMapToFrancePosition(map);
        }
        hasMapAlreadyBeenFitted.current = true;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- no need to wait for the result
        debouncedUpdateQueryVariables(false); // to be sure places = [the viewport of the map]

        // eslint-disable-next-line react-hooks/exhaustive-deps -- don't want to rerender each time map change but only when map is defined
    }, [adsQueryVariables.places, tilesLoaded, fitMapOnAdSearchStateChange]);

    const fitMapByAddress = async (addressToFitMap: string) => {
        if (!map) {
            return;
        }

        try {
            const geocodeResults = await geocodeByAddress(addressToFitMap);
            if (geocodeResults.length === 0) {
                fitMapToFrancePosition(map);
            } else {
                const addressGeometry = transformGoogleMapBoundsToGeometry(geocodeResults[0].geometry.viewport);
                fitMapToWoopenGeometry(map, addressGeometry);
            }
        } catch {
            fitMapToFrancePosition(map);
        } finally {
            hasMapAlreadyBeenFitted.current = true;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- no need to wait for the result
            debouncedUpdateQueryVariables(false); // to be sure places = [the viewport of the map]
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedUpdateQueryVariables = useCallback(
        debounce(async (byMovingTheMap = true) => {
            if (map) {
                const bounds = map.getBounds();
                const places: GeometryInput[] = [];
                let mapBoundsGeometry: GeometryInput | undefined;
                if (bounds) {
                    // We transform the map box in a "polygon" to have only ads in this box
                    mapBoundsGeometry = transformGoogleMapBoundsToGeometry(bounds);
                    places.push(mapBoundsGeometry as GeometryInput);
                    setBoundsForClustering({
                        bounds: transformGoogleMapBoundsToGeoJSONBBox(bounds),
                        zoom: map.getZoom(),
                    });
                }
                const newPlacesAreEquals = isEqual(places, adsQueryVariables.places);
                const shouldUpdateAdsQueryVariables =
                    !newPlacesAreEquals && places.length && !isDrawingMode && !isPolygonDisplayed && !savedSearchId;

                await changeAdSearchState({
                    ...(shouldUpdateAdsQueryVariables && {
                        adsQueryVariables: {
                            ...adsQueryVariables,
                            place: byMovingTheMap ? undefined : adsQueryVariables.place,
                            places,
                        },
                    }),
                    ...(mapBoundsGeometry && { mapBounds: mapBoundsGeometry }),
                    ...(byMovingTheMap === true && { fitMapOnAdSearchStateChange: false }),
                });
            }
        }, 250),
        [tilesLoaded, adsQueryVariables, isDrawingMode],
    );

    const { clusters, supercluster } = useClusters({
        ads: ads ? (ads as Ad[]) : [],
        boundsWithZoom: boundsForClustering,
        disableRefresh: loading,
        maxZoom,
    });

    const markers = useMemo(
        () =>
            clusters.map((cluster) => {
                const [lng, lat] = cluster.geometry.coordinates;
                const { cluster: isCluster, point_count: pointCount } = cluster.properties;
                let childrenIds;
                if (supercluster && typeof cluster.id === 'number') {
                    childrenIds = supercluster.getLeaves(cluster.id, Infinity).map(({ properties }) => properties.adId);
                }
                const mapIsOnMaxZoom = Boolean(map && map.getZoom() === maxZoom);
                return (
                    <SearchAdsPageMarker
                        key={`SearchAdsPageMarker-${cluster.id ?? cluster.properties.adId}`}
                        ad={!isCluster ? cluster.properties.ad : undefined}
                        cluster={
                            isCluster
                                ? {
                                      lat,
                                      lng,
                                      pointCount,
                                      childrenIds,
                                      mapIsOnMaxZoom,
                                      handleClickCluster() {
                                          if (supercluster && typeof cluster.id === 'number' && map) {
                                              const expansionZoom = Math.min(
                                                  supercluster.getClusterExpansionZoom(cluster.id),
                                                  maxZoom,
                                              );
                                              map.setZoom(expansionZoom);
                                              map.panTo({ lat, lng });
                                          }
                                          return null;
                                      },
                                      getClusterChildren() {
                                          if (supercluster && typeof cluster.id === 'number') {
                                              return supercluster.getLeaves(cluster.id, Infinity);
                                          }
                                          return null;
                                      },
                                  }
                                : undefined
                        }
                    />
                );
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clusters],
    );

    // Need to be memoized to avoid rerendering the component each time the user moves his mouse on the map
    const mapDrawingControl = useMemo(() => {
        if (!map) {
            return null;
        }
        return <SearchAdsPageMapDrawing map={map} isDrawingMode={isDrawingMode} setIsDrawingMode={setIsDrawingMode} />;
    }, [isDrawingMode, map]);

    return (
        <Container
            onMouseUp={() => changeAdSearchState({ fitMapOnAdSearchStateChange: false })}
            onMouseEnter={() => setMouseIsOnMap(true)}
            onMouseLeave={() => setMouseIsOnMap(false)}
        >
            <GoogleMap
                onZoomChanged={async () => {
                    // mouseIsOnMap is used to know if the user is responsible for the map zoom change
                    if (map && mouseIsOnMap) {
                        await changeAdSearchState({ fitMapOnAdSearchStateChange: false });
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        debouncedUpdateQueryVariables();
                    }
                }}
                onCenterChanged={!fitMapOnAdSearchStateChange ? debouncedUpdateQueryVariables : undefined}
                onFullscreenChange={(isFullscreen) => changeAdSearchState({ isMapFullscreen: isFullscreen })}
                setMap={setMap}
                options={{
                    ...googleMapDefaultOptions,
                    maxZoom,
                }}
            >
                {!isDrawingMode && markers}
            </GoogleMap>
            {mapDrawingControl}
        </Container>
    );
};
